(function () {
  'use strict';

  if (customElements.get('yog-vape-builder')) return;

  // Fallback strings; overridden by data-i18n (schema settings, translatable)
  const DEFAULT_I18N = {
    adding:       '追加中...',
    added:        '追加しました！',
    selectError:  'カートリッジとバッテリーを選択してください。',
    cartError:    'カートへの追加に失敗しました。もう一度お試しください。',
    networkError: '通信エラーが発生しました。接続を確認してもう一度お試しください。',
    soldout:      'この商品は現在在庫切れです。',
    initError:    '読み込みに失敗しました。ページを再読み込みしてください。',
    noneSummary:  'バッテリーなし'
  };

  class YogVapeBuilder extends HTMLElement {
    constructor() {
      super();
      this._cartridges = [];
      this._batteries  = [];
      this._selected   = { cartridge: null, battery: null };   // { blockId, variantId, price, layerImage, layerScale, layerOffsetY, fallbackImage, title }
      this._moneyFormat = '${{amount}}';
      this._busy       = false;   // add-to-cart in flight (blocks double submits)
      // Cartridge-only (no battery) pose: upright & centered, front-facing
      this._soloScale   = 72;
      this._soloOffsetY = -4;
    }

    connectedCallback() {
      let i18n = {};
      try { i18n = JSON.parse(this.dataset.i18n || '{}'); } catch (e) { /* fall back to defaults */ }
      this._t = Object.assign({}, DEFAULT_I18N, i18n);

      let initFailed = false;
      try {
        this._cartridges  = JSON.parse(this.dataset.cartridges  || '[]');
        this._batteries   = JSON.parse(this.dataset.batteries   || '[]');
      } catch (e) {
        console.error('[YogVapeBuilder] JSON parse error', e);
        initFailed = true;
      }
      // Normalize so item.variants is always an array
      const norm = (list) => (Array.isArray(list) ? list : []).map(item =>
        Object.assign({}, item, { variants: Array.isArray(item.variants) ? item.variants : [] }));
      this._cartridges = norm(this._cartridges);
      this._batteries  = norm(this._batteries);
      this._moneyFormat = this.dataset.moneyFormat || (window.theme && window.theme.moneyFormat) || '¥{{amount}}';
      // Volume codes come from the section's single source (vb_volume_codes)
      this._volumeCodes = (this.dataset.volumeCodes || '03,05,10').split(',');

      // Broken product data: tell the user instead of failing silently on every click
      if (initFailed) {
        this._showError(this._t.initError);
        this.querySelectorAll('[data-card]').forEach(b => { b.disabled = true; });
        return;
      }

      this._bindEvents();
      this._setBatteryLock(true);   // battery locked until a cartridge is chosen
      this._setStickyOffset();      // offset the mobile sticky preview below a sticky header
    }

    // If the theme has a sticky/fixed header, pin the mobile preview just below it.
    _setStickyOffset() {
      try {
        const hdr = document.querySelector('[data-site-header], .site-header, header');
        let top = 10;
        if (hdr) {
          const pos = getComputedStyle(hdr).position;
          if (pos === 'sticky' || pos === 'fixed') {
            top = (parseInt(hdr.dataset && hdr.dataset.height, 10) || hdr.offsetHeight || 56) + 8;
          }
        }
        this.style.setProperty('--vb-sticky-top', top + 'px');
      } catch (e) { /* keep CSS default */ }
    }

    // ─── Event binding ───────────────────────────────────────────────────────

    _bindEvents() {
      // Card clicks (cartridge / battery)
      this.querySelectorAll('[data-card]').forEach(btn => {
        btn.addEventListener('click', () => this._onCardClick(btn));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onCardClick(btn);
          }
        });
      });

      // ATC button
      const atcBtn = this.querySelector('[data-atc-btn]');
      if (atcBtn) atcBtn.addEventListener('click', () => this._onAddToCart());
    }

    // ─── Card selection ──────────────────────────────────────────────────────

    _onCardClick(btn) {
      const group    = btn.dataset.card;   // 'cartridge' or 'battery'

      // Enforce cartridge-first: block any battery choice until a cartridge is chosen
      if (group === 'battery' && !this._selected.cartridge) {
        this._showBatteryWarning(true);
        return;
      }

      // "No battery" option (cartridge only)
      if (btn.dataset.none === 'true') {
        this.querySelectorAll(`[data-card="${group}"]`).forEach(b => {
          b.setAttribute('aria-checked', 'false');
          b.classList.remove('is-selected');
        });
        btn.setAttribute('aria-checked', 'true');
        btn.classList.add('is-selected');

        this._selected.battery = { none: true, variantId: null, price: 0, layerImage: null, title: 'なし' };

        // Hide the color/variant selector
        const wrap = this.querySelector('[data-variant-wrap="battery"]');
        if (wrap) wrap.classList.remove('is-visible');

        this._clearError();
        this._refreshPreview();
        this._updateTotal();
        this._updateAtcState();
        return;
      }

      const blockId  = btn.dataset.blockId;
      const dataList = group === 'cartridge' ? this._cartridges : this._batteries;
      const item     = dataList.find(d => d.blockId === blockId);
      if (!item) return;

      // Deselect others in same group
      this.querySelectorAll(`[data-card="${group}"]`).forEach(b => {
        b.setAttribute('aria-checked', 'false');
        b.classList.remove('is-selected');
      });
      btn.setAttribute('aria-checked', 'true');
      btn.classList.add('is-selected');

      // Default variant = first available. If everything is sold out, keep
      // variantId null so _updateAtcState() disables the ATC button (previously the
      // first sold-out variant was silently selected and only failed at cart time).
      const firstAvail = item.variants.find(v => v.available) || null;
      const defIdx     = firstAvail ? item.variants.indexOf(firstAvail) : 0;

      const variantLayers = item.variantLayers || [];
      const variantLayer  = variantLayers[defIdx] || null;

      this._selected[group] = {
        blockId:      item.blockId,
        variantId:    firstAvail ? firstAvail.id   : null,
        price:        firstAvail ? firstAvail.price : 0,
        layerImage:   variantLayer || item.layerImage || item.fallbackImage || null,
        layerScale:   item.layerScale   || 100,
        layerOffsetY: item.layerOffsetY || 0,
        layerOffsetX: item.layerOffsetX || 0,
        layerRotate:  item.layerRotate  || 0,
        fallbackImage: item.fallbackImage || null,
        standalone:   !!item.standalone,
        variantLayers: variantLayers,
        volumeMap:    item.volumeMap || null,
        colorVolumeMap: item.colorVolumeMap || null,
        colorIndex:   defIdx,
        variantTitle: (item.variants.length > 1 && firstAvail) ? firstAvail.title : '',
        title:        item.title,
      };

      if (!firstAvail) {
        this._showError(this._t.soldout);
      } else {
        this._clearError();
      }

      // Selecting a cartridge unlocks the battery group
      if (group === 'cartridge') {
        this._showBatteryWarning(false);
        this._setBatteryLock(false);
      }

      this._renderVariantSelector(group, item);
      this._refreshPreview();
      this._updateTotal();
      this._updateAtcState();
    }

    // ─── Variant selector ────────────────────────────────────────────────────

    _renderVariantSelector(group, item) {
      const wrap    = this.querySelector(`[data-variant-wrap="${group}"]`);
      const optWrap = this.querySelector(`[data-variant-options="${group}"]`);
      if (!wrap || !optWrap) return;

      // Single-variant products: hide the selector
      if (item.variants.length <= 1) {
        wrap.classList.remove('is-visible');
        return;
      }

      optWrap.innerHTML = '';
      item.variants.forEach((variant, idx) => {
        const btn = document.createElement('button');
        btn.className = 'vb-variant-btn';
        btn.type      = 'button';
        btn.textContent = variant.title;
        btn.disabled  = !variant.available;
        btn.dataset.variantId    = variant.id;
        btn.dataset.variantPrice = variant.price;

        // Mark first-selected
        if (variant.id === this._selected[group].variantId) {
          btn.classList.add('is-selected');
        }

        btn.addEventListener('click', () => this._onVariantClick(group, item, variant, btn, idx));
        optWrap.appendChild(btn);
      });

      wrap.classList.add('is-visible');
    }

    _onVariantClick(group, item, variant, btn, index) {
      // Update button states
      const optWrap = this.querySelector(`[data-variant-options="${group}"]`);
      if (optWrap) {
        optWrap.querySelectorAll('.vb-variant-btn').forEach(b => b.classList.remove('is-selected'));
      }
      btn.classList.add('is-selected');

      // Update selected state
      if (this._selected[group]) {
        this._selected[group].variantId = variant.id;
        this._selected[group].price     = variant.price;
        this._selected[group].colorIndex = index;
        this._selected[group].variantTitle = variant.title;

        // Swap the layer image for this variant (e.g. battery color silver/white/black).
        // If this variant has no dedicated image, fall back to the default (first) layer
        // so the preview still reacts instead of freezing on the previous color.
        const layers = this._selected[group].variantLayers || [];
        const newLayer = layers[index] || layers[0] || this._selected[group].layerImage;
        if (newLayer) {
          this._selected[group].layerImage = newLayer;
        }
      }

      this._refreshPreview();
      this._updateTotal();
      this._updateAtcState();
    }

    // ─── Preview ─────────────────────────────────────────────────────────────

    // Apply one layer image (battery or cartridge) with its scale + vertical offset.
    _applyLayer(imgEl, sel, visible) {
      if (!imgEl) return;
      const src = sel ? (sel.layerImage || sel.fallbackImage) : null;
      if (!visible || !sel || !src) {
        imgEl.style.display = 'none';
        return;
      }
      // On 404: retry once with the product photo, then hide instead of showing a broken image
      imgEl.onerror = () => {
        if (sel.fallbackImage && src !== sel.fallbackImage) {
          imgEl.onerror = () => { imgEl.onerror = null; imgEl.style.display = 'none'; };
          imgEl.src = sel.fallbackImage;
        } else {
          imgEl.onerror = null;
          imgEl.style.display = 'none';
        }
      };
      imgEl.src           = src;
      imgEl.alt           = sel.title || '';
      imgEl.style.display = '';
      const scale   = (sel.layerScale || 100) / 100;
      const offsetY = (sel.layerOffsetY || 0);
      const offsetX = (sel.layerOffsetX || 0);
      const rotate  = (sel.layerRotate  || 0);
      imgEl.style.transform       = `translate(${offsetX}%, ${offsetY}%) rotate(${rotate}deg) scale(${scale})`;
      imgEl.style.transformOrigin = 'center center';
    }

    // Rebuild the whole composite. Handles two modes:
    //  - stack (default): battery (bottom) + cartridge (top) with a gap between them
    //  - standalone (box batteries): show the battery scene alone, hide the cartridge layer
    _refreshPreview() {
      const cartImg   = this.querySelector('[data-preview-img="cartridge"]');
      const battImg   = this.querySelector('[data-preview-img="battery"]');
      const placeholder = this.querySelector('[data-preview-placeholder]');

      const cartSel = this._selected.cartridge;
      const battSel = this._selected.battery;
      const standalone = !!(battSel && battSel.standalone);

      // For standalone batteries, swap the composite image to match the selected
      // cartridge volume (derived from the cartridge layer filename: vb-cartridge-03/05/10).
      // - colorVolumeMap (airis): pick by [selected color variant index][volume]
      // - volumeMap (Pro/CCELL): pick by [volume]
      let battEffective = battSel;
      if (standalone && battSel) {
        const m = cartSel && cartSel.layerImage ? cartSel.layerImage.match(/-(\d{2,})\.png/) : null;
        const codes = this._volumeCodes || ['03', '05', '10'];
        // Only accept codes the section actually knows; default to 0.5ml otherwise
        const code = (m && codes.indexOf(m[1]) !== -1)
          ? m[1]
          : (codes.indexOf('05') !== -1 ? '05' : codes[0]);
        let url = null;
        if (Array.isArray(battSel.colorVolumeMap)) {
          const cm = battSel.colorVolumeMap[battSel.colorIndex || 0];
          url = cm ? cm[code] : null;
        }
        // colorVolumeMap exists (as an empty array) on every battery, so fall back to
        // the simple volumeMap when it did not yield an image (Pro / CCELL box batteries).
        if (!url && battSel.volumeMap) {
          url = battSel.volumeMap[code];
        }
        if (url) battEffective = Object.assign({}, battSel, { layerImage: url });
      }

      // Battery layer always shows when selected
      this._applyLayer(battImg, battEffective, !!battSel);

      // Cartridge layer: hidden in standalone (box) mode. When a cartridge is shown
      // WITHOUT a stacked battery (no battery yet, or "no battery"), display it
      // upright & centered (front-facing) instead of the diagonal combo pose.
      const batteryStacked = battSel && !battSel.none && !standalone;
      let cartEffective = cartSel;
      if (cartSel && !standalone && !batteryStacked) {
        cartEffective = Object.assign({}, cartSel, {
          layerRotate:  0,
          layerOffsetX: 0,
          layerOffsetY: this._soloOffsetY,
          layerScale:   this._soloScale,
        });
      }
      this._applyLayer(cartImg, cartEffective, !!cartSel && !standalone);

      if (placeholder) {
        placeholder.style.display = (cartSel || battSel) ? 'none' : '';
      }
    }

    // ─── Total ───────────────────────────────────────────────────────────────

    _updateTotal() {
      const totalEl = this.querySelector('[data-total-price]');
      const cPrice = this._selected.cartridge ? (this._selected.cartridge.price || 0) : 0;
      const bPrice = this._selected.battery   ? (this._selected.battery.price   || 0) : 0;
      if (totalEl) {
        totalEl.textContent = (!cPrice && !bPrice) ? '--' : this._formatMoney(cPrice + bPrice);
      }
      this._updateSummary();
    }

    // Show the chosen cartridge / battery (name + variant + price) above the total.
    _updateSummary() {
      const el = this.querySelector('[data-summary]');
      if (!el) return;
      const rows = [];
      const c = this._selected.cartridge;
      const b = this._selected.battery;
      if (c) rows.push(this._summaryRow(c.title, c.variantTitle, c.price));
      if (b) {
        if (b.none) rows.push(this._summaryRow(this._t.noneSummary, '', 0));
        else        rows.push(this._summaryRow(b.title, b.variantTitle, b.price));
      }
      el.innerHTML = rows.join('');
      el.hidden = rows.length === 0;
    }

    _summaryRow(name, variant, priceCents) {
      const price = priceCents ? this._formatMoney(priceCents) : '¥0';
      const v = variant ? `<span>${this._esc(variant)}</span>` : '';
      return `<li><span class="vb-summary-name"><b>${this._esc(name)}</b>${v}</span>` +
             `<span class="vb-summary-price">${price}</span></li>`;
    }

    _esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, c =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    }

    _showBatteryWarning(show) {
      const w = this.querySelector('[data-battery-warning]');
      if (w) w.hidden = !show;
    }

    _setBatteryLock(locked) {
      const g = this.querySelector('[data-battery-group]');
      if (g) g.classList.toggle('is-locked', locked);
    }

    _formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents, this._moneyFormat);
      }
      // Fallback: simple yen display
      return '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
    }

    // ─── ATC state ───────────────────────────────────────────────────────────

    _updateAtcState() {
      const btn = this.querySelector('[data-atc-btn]');
      if (!btn) return;
      const batt = this._selected.battery;
      const batteryReady = batt && (batt.variantId || batt.none);
      const ready = this._selected.cartridge && this._selected.cartridge.variantId && batteryReady;
      btn.disabled = !ready;
    }

    // ─── Add to cart ─────────────────────────────────────────────────────────

    async _onAddToCart() {
      if (this._busy) return;   // block double clicks / concurrent submits
      const btn = this.querySelector('[data-atc-btn]');
      if (!btn) return;

      const batt       = this._selected.battery;
      const cVariantId = this._selected.cartridge && this._selected.cartridge.variantId;
      const bVariantId = batt && batt.variantId;
      const noBattery  = !!(batt && batt.none);

      if (!cVariantId || (!bVariantId && !noBattery)) {
        this._showError(this._t.selectError);
        return;
      }

      // Build line items: always the cartridge; battery only if one is chosen
      const items = [{ id: cVariantId, quantity: 1 }];
      if (bVariantId && !noBattery) {
        items.push({ id: bVariantId, quantity: 1 });
      }

      // Loading state
      this._busy = true;
      const originalText = btn.textContent;
      btn.disabled       = true;
      btn.textContent    = this._t.adding;
      this._clearError();

      try {
        const response = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || err.message || `HTTP ${response.status}`);
        }

        // Notify the theme's cart system
        // Palo Alto listens to "theme:cart:add" with detail.button to open the drawer
        document.dispatchEvent(
          new CustomEvent('theme:cart:add', { bubbles: true, detail: { button: btn } })
        );

        btn.textContent = this._t.added;
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled    = false;
        }, 2000);

      } catch (error) {
        console.error('[YogVapeBuilder] Cart add error', error);
        // fetch itself failing (TypeError) means a network problem; anything else
        // carries the server-side reason (e.g. Shopify 422 stock message)
        const msg = (error instanceof TypeError)
          ? this._t.networkError
          : (error.message || this._t.cartError);
        this._showError(msg);
        btn.textContent = originalText;
        btn.disabled    = false;
      } finally {
        this._busy = false;
      }
    }

    _showError(msg) {
      const el = this.querySelector('[data-error-msg]');
      if (!el) return;
      el.textContent = msg;
      el.classList.add('is-visible');
    }

    _clearError() {
      const el = this.querySelector('[data-error-msg]');
      if (!el) return;
      el.textContent = '';
      el.classList.remove('is-visible');
    }
  }

  customElements.define('yog-vape-builder', YogVapeBuilder);
})();
